/*
 * Рамка аватара — ПОРТ из Майнкрафтии 2.0 (design/components/AvatarFrame.tsx).
 *
 * Зачем здесь копия: у школы фронт на Vite+TS, у редактора — CRA+JS, общей
 * сборки нет. Компонент самодостаточный (только React и CSS-keyframes), так что
 * перенос дешевле, чем вытаскивать его в общий пакет ради одного места.
 *
 * Рамки — НЕ картинки, а чистый CSS. Именно поэтому в старой шапке робототехники
 * рамка МИНа («galaxy», донатная за 80 звёзд) не отображалась вовсе: там стоял
 * голый <img> без этого компонента.
 *
 * ⚠️ При изменении рамок в школе — синхронизировать этот файл.
 */
/*
 * Рамка вокруг аватара (косметика из магазина профиля).
 *
 * Базовые рамки — статичные или с вращающимся градиентом. Премиум — с «живым»
 * движением: точки-спутники на орбите, кометы с хвостами, бегущая гирлянда,
 * ползущая дуга-змейка, мерцающая звёздная пыль, летающий котик, корона.
 * Всё на чистом CSS (keyframes) — лёгкое, обтекает аватар любого размера.
 *
 * Портировано из Маськи (первоисточник косметики), приведено к TS.
 * frame — ключ рамки ("gold", "orbit"…) или style-строка ("solid:#hex").
 */

// Конфиг рамок: kind — тип эффекта; остальное — параметры.
export const FRAME_CFG = {
  none:    { kind: "none" },
  silver:  { kind: "solid", colors: ["#c0c5cc"] },
  gold:    { kind: "solid", colors: ["#f2c14e"] },
  coral:   { kind: "solid", colors: ["#e8445a"] },
  ocean:   { kind: "spin",  colors: ["#37c6ff", "#3b82f6", "#00e5ff", "#37c6ff"], dur: 4 },
  sunset:  { kind: "spin",  colors: ["#ff7a8c", "#f4a261", "#ffd166", "#ff7a8c"], dur: 5 },
  neon:    { kind: "glow",  colors: ["#00e5ff", "#a855f7"], glow: "#a855f7" },
  rainbow: { kind: "rainbow" },
  fire:    { kind: "fire",  colors: ["#ff512f", "#f09819", "#ffd166"] },
  legend:  { kind: "legend" },
  // — премиум с движением —
  orbit:     { kind: "orbit",   base: "#1b2a4a", dot: "#37c6ff", dur: 3 },
  comets:    { kind: "comets",  base: "#2a1a3a", dot: "#ff5db1", dur: 2.4 },
  runlights: { kind: "runlights", base: "#222", on: "#ffd166", off: "#7a5a10" },
  snake:     { kind: "snake",   base: "#0d2a1a", glow: "#39ff14", dur: 2.6 },
  stardust:  { kind: "stardust", base: "#1a1030", dot: "#ffe6a8", dur: 5 },
  maska:     { kind: "cat",     base: "#3a1530", dot: "#ff7ac0", dur: 4 },
  // — премиум за ЗВЁЗДЫ (донат) —
  royal:   { kind: "royal",   base: "#3a2e05", glow: "#ffd23a", dur: 3 },
  galaxy:  { kind: "galaxy",  base: "#140a2e", dot: "#b98bff", dur: 6 },
  phoenix: { kind: "phoenix", base: "#3a0f05", dot: "#ff7a2f", dur: 2.4 },
};

// keyframes (вставляются один раз через <style>)
const KEYFRAMES = `
@keyframes mc-frame-spin { to { transform: rotate(360deg); } }
@keyframes mc-frame-glow {
  0%,100% { box-shadow: 0 0 4px 1px var(--mf-glow), inset 0 0 3px var(--mf-glow); }
  50%     { box-shadow: 0 0 12px 4px var(--mf-glow), inset 0 0 6px var(--mf-glow); }
}
@keyframes mc-frame-flicker {
  0%,100% { filter: brightness(1)   saturate(1.1); }
  40%     { filter: brightness(1.3) saturate(1.4); }
  70%     { filter: brightness(0.95) saturate(1.2); }
}
@keyframes mc-orbit-twinkle { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
@keyframes mc-runlight { 0%,100% { opacity: 0.25; } 50% { opacity: 1; } }
`;

// Вращающийся контейнер, в котором «спутник» висит на радиусе.
function Spinner({ dur, delay = 0, reverse = false, children }) {
  return (
    <div style={{
      position: "absolute", inset: 0, borderRadius: "50%",
      animation: `mc-frame-spin ${dur}s linear ${delay}s infinite${reverse ? " reverse" : ""}`,
    }}>
      {children}
    </div>
  );
}

// Точка-спутник на верхней кромке кольца (за счёт вращения родителя — летит по кругу)
function Satellite({ color, size, glow = true, tail = false, content = null }) {
  return (
    <div style={{
      position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
      width: tail ? size * 4 : size, height: size, borderRadius: size,
      background: tail ? `linear-gradient(90deg, transparent, ${color})` : color,
      boxShadow: glow ? `0 0 ${size * 1.6}px ${size * 0.4}px ${color}` : "none",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size, lineHeight: 1,
    }}>
      {content}
    </div>
  );
}

export function AvatarFrame({ frame, size = 48, thickness = 3, children, style }) {
  const key = frame || "none";
  const cfg = FRAME_CFG[key];

  // нет рамки / неизвестный ключ без style-строки — просто центрируем аватар,
  // чтобы он не прижимался в угол (children может быть меньше size).
  if (key === "none" || (!cfg && !(typeof key === "string" && key.includes(":")))) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0, ...style }}>
        {children}
      </div>
    );
  }

  const ringStyle = {
    width: size, height: size, borderRadius: "50%",
    padding: thickness, boxSizing: "border-box", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", ...style,
  };

  const avatar = (
    <div style={{
      position: "relative", zIndex: 2,
      width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden",
      boxShadow: "0 0 0 1.5px rgba(255,255,255,0.6) inset",
    }}>
      {children}
    </div>
  );

  const dotSize = Math.max(3, Math.round(size * 0.09));

  let ringBg = null;
  let bgAnimation = null;
  let glowColor = null;
  let movers = null;

  if (!cfg) {
    if (key.startsWith("solid:")) ringBg = key.slice(6);
    else if (key.startsWith("gradient:")) ringBg = `linear-gradient(135deg, ${key.slice(9)})`;
    else if (key === "rainbow") ringBg = "conic-gradient(#ff0040,#ff8a00,#ffe600,#00d26a,#00b3ff,#a855f7,#ff0040)";
  } else if (cfg.kind === "solid") {
    ringBg = cfg.colors[0];
  } else if (cfg.kind === "spin") {
    ringBg = `conic-gradient(${cfg.colors.join(",")})`;
    bgAnimation = `mc-frame-spin ${cfg.dur}s linear infinite`;
  } else if (cfg.kind === "glow") {
    ringBg = `linear-gradient(135deg, ${cfg.colors.join(",")})`;
    glowColor = cfg.glow;
  } else if (cfg.kind === "rainbow") {
    ringBg = "conic-gradient(#ff0040,#ff8a00,#ffe600,#00d26a,#00b3ff,#a855f7,#ff0040)";
    bgAnimation = "mc-frame-spin 3s linear infinite";
  } else if (cfg.kind === "fire") {
    ringBg = `conic-gradient(${cfg.colors.join(",")},${cfg.colors[0]})`;
    bgAnimation = "mc-frame-spin 6s linear infinite, mc-frame-flicker 1.2s ease-in-out infinite";
  } else if (cfg.kind === "legend") {
    ringBg = "conic-gradient(#f2c14e,#ff8a00,#e8445a,#a855f7,#f2c14e)";
    bgAnimation = "mc-frame-spin 4s linear infinite, mc-frame-flicker 1.5s ease-in-out infinite";
    glowColor = "#f2c14e";
  } else if (cfg.kind === "orbit") {
    ringBg = `radial-gradient(circle, ${cfg.base}, ${cfg.base})`;
    glowColor = cfg.dot;
    movers = <Spinner dur={cfg.dur}><Satellite color={cfg.dot} size={dotSize * 1.3} /></Spinner>;
  } else if (cfg.kind === "comets") {
    ringBg = cfg.base;
    glowColor = cfg.dot;
    movers = (
      <>
        <Spinner dur={cfg.dur}><Satellite color={cfg.dot} size={dotSize} tail /></Spinner>
        <Spinner dur={cfg.dur} delay={-cfg.dur / 3}><Satellite color="#ffd166" size={dotSize} tail /></Spinner>
        <Spinner dur={cfg.dur} delay={-2 * cfg.dur / 3}><Satellite color="#7af" size={dotSize} tail /></Spinner>
      </>
    );
  } else if (cfg.kind === "runlights") {
    ringBg = cfg.base;
    const N = 12;
    movers = (
      <div style={{ position: "absolute", inset: 0 }}>
        {Array.from({ length: N }).map((_, i) => (
          <div key={i} style={{ position: "absolute", inset: 0, transform: `rotate(${(360 / N) * i}deg)` }}>
            <div style={{
              position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
              width: dotSize, height: dotSize, borderRadius: dotSize,
              background: cfg.on, boxShadow: `0 0 ${dotSize}px ${cfg.on}`,
              animation: `mc-runlight 1s linear ${(i / N) * 1}s infinite`,
            }} />
          </div>
        ))}
      </div>
    );
  } else if (cfg.kind === "snake") {
    ringBg = `conic-gradient(${cfg.glow} 0deg, ${cfg.glow} 40deg, ${cfg.base} 60deg, ${cfg.base} 360deg)`;
    bgAnimation = `mc-frame-spin ${cfg.dur}s linear infinite`;
    glowColor = cfg.glow;
  } else if (cfg.kind === "stardust") {
    ringBg = `radial-gradient(circle, ${cfg.base}, ${cfg.base})`;
    const N = 8;
    movers = (
      <Spinner dur={cfg.dur}>
        {Array.from({ length: N }).map((_, i) => (
          <div key={i} style={{ position: "absolute", inset: 0, transform: `rotate(${(360 / N) * i}deg)` }}>
            <div style={{
              position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
              width: dotSize * 0.8, height: dotSize * 0.8, borderRadius: dotSize,
              background: cfg.dot, boxShadow: `0 0 ${dotSize}px ${cfg.dot}`,
              animation: `mc-orbit-twinkle ${1 + (i % 3) * 0.4}s ease-in-out ${i * 0.2}s infinite`,
            }} />
          </div>
        ))}
      </Spinner>
    );
  } else if (cfg.kind === "cat") {
    ringBg = "conic-gradient(#ff7ac0,#ffd1ec,#ff7ac0,#c44ea0,#ff7ac0)";
    bgAnimation = "mc-frame-spin 8s linear infinite";
    glowColor = cfg.dot;
    const catSize = Math.max(10, Math.round(size * 0.24));
    movers = (
      <Spinner dur={cfg.dur}>
        <div style={{
          position: "absolute", top: -catSize * 0.5, left: "50%", transform: "translateX(-50%)",
          width: catSize, height: catSize, fontSize: catSize, lineHeight: 1,
          filter: "drop-shadow(0 0 3px #ff7ac0)",
        }}>🐱</div>
      </Spinner>
    );
  } else if (cfg.kind === "royal") {
    ringBg = "conic-gradient(#ffe98a,#ffd23a,#c9962a,#ffd23a,#ffe98a)";
    bgAnimation = `mc-frame-spin ${cfg.dur * 2}s linear infinite, mc-frame-glow 2s ease-in-out infinite`;
    glowColor = cfg.glow;
    const N = 8;
    movers = (
      <div style={{ position: "absolute", inset: 0 }}>
        {Array.from({ length: N }).map((_, i) => (
          <div key={i} style={{ position: "absolute", inset: 0, transform: `rotate(${(360 / N) * i}deg)` }}>
            <div style={{
              position: "absolute", top: -dotSize * 0.5, left: "50%", transform: "translateX(-50%)",
              width: 0, height: 0,
              borderLeft: `${dotSize * 0.5}px solid transparent`,
              borderRight: `${dotSize * 0.5}px solid transparent`,
              borderBottom: `${dotSize}px solid ${cfg.glow}`,
              filter: `drop-shadow(0 0 ${dotSize * 0.6}px ${cfg.glow})`,
              animation: `mc-orbit-twinkle ${1.4 + (i % 3) * 0.3}s ease-in-out ${i * 0.15}s infinite`,
            }} />
          </div>
        ))}
      </div>
    );
  } else if (cfg.kind === "galaxy") {
    ringBg = "conic-gradient(#2a1060,#6a3bd0,#3b7bd0,#b98bff,#2a1060)";
    bgAnimation = `mc-frame-spin ${cfg.dur}s linear infinite`;
    glowColor = cfg.dot;
    const N = 10;
    movers = (
      <>
        <Spinner dur={cfg.dur * 0.6}>
          {Array.from({ length: N }).map((_, i) => (
            <div key={i} style={{ position: "absolute", inset: 0, transform: `rotate(${(360 / N) * i}deg)` }}>
              <div style={{
                position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
                width: dotSize * 0.6, height: dotSize * 0.6, borderRadius: dotSize,
                background: "#fff", boxShadow: `0 0 ${dotSize}px ${cfg.dot}`,
                animation: `mc-orbit-twinkle ${1 + (i % 3) * 0.4}s ease-in-out ${i * 0.2}s infinite`,
              }} />
            </div>
          ))}
        </Spinner>
        <Spinner dur={cfg.dur} delay={-cfg.dur / 2}>
          <Satellite color={cfg.dot} size={dotSize * 1.2} tail />
        </Spinner>
      </>
    );
  } else if (cfg.kind === "phoenix") {
    ringBg = "conic-gradient(#ff2f00,#ff7a2f,#ffd166,#ff512f,#ff2f00)";
    bgAnimation = `mc-frame-spin ${cfg.dur * 1.5}s linear infinite, mc-frame-flicker 1.2s ease-in-out infinite`;
    glowColor = cfg.dot;
    movers = (
      <>
        <Spinner dur={cfg.dur}><Satellite color="#ffd166" size={dotSize} tail /></Spinner>
        <Spinner dur={cfg.dur} delay={-cfg.dur / 3}><Satellite color="#ff7a2f" size={dotSize} tail /></Spinner>
        <Spinner dur={cfg.dur} delay={-2 * cfg.dur / 3}><Satellite color="#ff512f" size={dotSize} tail /></Spinner>
      </>
    );
  }

  const spinning = !!bgAnimation && bgAnimation.includes("spin");
  const glowVar = glowColor ? ({ "--mf-glow": glowColor }) : {};

  return (
    <div style={ringStyle}>
      <style>{KEYFRAMES}</style>
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%", zIndex: 0,
        background: ringBg || undefined,
        animation: bgAnimation || undefined,
        ...glowVar,
        ...(glowColor && !spinning && !movers ? { animation: "mc-frame-glow 1.6s ease-in-out infinite" } : {}),
        ...(glowColor && (spinning || movers) ? { boxShadow: `0 0 10px 2px ${glowColor}` } : {}),
      }} />
      {movers && <div style={{ position: "absolute", inset: 0, zIndex: 1, borderRadius: "50%" }}>{movers}</div>}
      {avatar}
    </div>
  );
}
